import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/contexts/ThemeContext";
import { apiService } from "@/services/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SelectField } from "@/components/ui/select";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FileText,
  Plus,
  Search,
  Edit,
  Trash2,
  Calendar,
  RefreshCw,
  Save,
  Copy,
} from "lucide-react";

interface NotificationTemplate {
  _id: string;
  title: string;
  body: string;
  document?: string;
  category?: string;
  createdAt: string;
  updatedAt: string;
}

interface PaginatedResponse {
  prev: boolean;
  next: boolean;
  page: number;
  item_per_page: number;
  total_page: number;
  total_count: number;
  history: NotificationTemplate[];
}

const TEMPLATE_CATEGORIES = [
  "general",
  "promotional",
  "system",
  "reminder",
  "alert",
  "welcome",
  "update",
];

export function Templates() {
  const { currentTheme } = useTheme();
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    body: "",
    category: "",
  });

  // Load templates
  const loadTemplates = async (page = 1) => {
    setLoading(true);
    try {
      const response = await apiService.getAllTemplates({ page });
      if (response.type === 0) {
        const data = response.data as PaginatedResponse;
        setTemplates(data.history);
        setCurrentPage(data.page);
        setTotalPages(data.total_page);
        setTotalCount(data.total_count);
      } else {
        toast.error(response.message || "Failed to load templates");
      }
    } catch (error) {
      toast.error("Failed to load templates");
      console.error("Error loading templates:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadTemplates();
  }, []);

  // Create template
  const handleCreateTemplate = async () => {
    if (!formData.title || !formData.body) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await apiService.createTemplate(formData);
      if (response.type === 0) {
        toast.success("Template created successfully");
        setIsCreateDialogOpen(false);
        setFormData({ title: "", body: "", category: "" });
        void loadTemplates(currentPage);
      } else {
        toast.error(response.message || "Failed to create template");
      }
    } catch (error) {
      toast.error("Failed to create template");
      console.error("Error creating template:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update template
  const handleUpdateTemplate = async () => {
    if (!selectedTemplate || !formData.title || !formData.body) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (!selectedTemplate._id) {
        toast.error("Missing template id");
        return;
      }
      
      console.log("Updating template ID:", selectedTemplate._id);
      console.log("Form data:", formData);
      
      setIsSubmitting(true);
      const response = await apiService.updateTemplate(selectedTemplate._id, formData);
      
      console.log("Update response:", response);
      
      if (response.type === 0) {
        toast.success("Template updated successfully");
        setIsEditDialogOpen(false);
        setSelectedTemplate(null);
        setFormData({ title: "", body: "", category: "" });
        void loadTemplates(currentPage);
      } else {
        toast.error(response.message || "Failed to update template");
        console.error("Update failed with response:", response);
      }
    } catch (error) {
      toast.error("Failed to update template");
      console.error("Error updating template:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete template
  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm("Are you sure you want to delete this template?")) return;

    try {
      if (!templateId) {
        toast.error("Missing template id");
        return;
      }
      
      console.log("Deleting template ID:", templateId);
      setIsSubmitting(true);
      
      const response = await apiService.deleteTemplate(templateId);
      console.log("Delete response:", response);
      
      if (response.type === 0) {
        toast.success("Template deleted successfully");
        void loadTemplates(currentPage);
      } else {
        toast.error(response.message || "Failed to delete template");
        console.error("Delete failed with response:", response);
      }
    } catch (error) {
      toast.error("Failed to delete template");
      console.error("Error deleting template:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit template
  const handleEditTemplate = (template: NotificationTemplate) => {
    setFormData({
      title: template.title,
      body: template.body,
      category: template.category ?? "",
    });
    setSelectedTemplate(template);
    setIsEditDialogOpen(true);
  };

  // Copy template
  const handleCopyTemplate = (template: NotificationTemplate) => {
    setFormData({
      title: `${template.title} (Copy)`,
      body: template.body,
      category: template.category ?? "",
    });
    setIsCreateDialogOpen(true);
  };

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const matchesSearch =
      template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.body.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getCategoryLabel = (category: string) => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  const truncateText = (text: string, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Notification Templates</h1>
          <p className="text-muted-foreground">
            Manage notification templates for different purposes
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => {
              void loadTemplates(currentPage);
            }}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button style={{ backgroundColor: currentTheme.colors.primary }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Template
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[100vw] max-w-6xl max-h-[92vh] overflow-auto">
              <DialogHeader>
                <DialogTitle>Add New Template</DialogTitle>
                <DialogDescription>Create a new notification template</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mb-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    placeholder="Enter template title..."
                    value={formData.title}
                    onChange={e => {
                      setFormData(prev => ({ ...prev, title: e.target.value }));
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <SelectField
                    value={formData.category}
                    onValueChange={(value: string) => {
                      setFormData(prev => ({ ...prev, category: value }));
                    }}
                    placeholder="Select category"
                    options={TEMPLATE_CATEGORIES.map(category => ({
                      label: getCategoryLabel(category),
                      value: category,
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="body">Message Body *</Label>
                  <div className="bg-white rounded border pb-4">
                    <ReactQuill
                      theme="snow"
                      value={formData.body}
                      onChange={(value: string) => {
                        setFormData(prev => ({ ...prev, body: value }));
                      }}
                      style={{ height: 560 }}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreateDialogOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateTemplate}
                  style={{ backgroundColor: currentTheme.colors.primary }}
                  disabled={isSubmitting}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Create Template
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search templates by title or content..."
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                }}
                className="pl-10"
              />
            </div>
            <div className="w-48">
              <SelectField
                value={selectedCategory}
                onValueChange={(value: string) => {
                  setSelectedCategory(value);
                }}
                placeholder="Filter by category"
                options={[
                  { label: "All Categories", value: "" },
                  ...TEMPLATE_CATEGORIES.map(category => ({
                    label: getCategoryLabel(category),
                    value: category,
                  })),
                ]}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates Table */}
      <Card>
        <CardHeader>
          <CardTitle>Notification Templates</CardTitle>
          <CardDescription>{totalCount} total templates</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              Loading templates...
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No templates found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Message Preview</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTemplates.map(template => (
                    <TableRow key={template._id}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <FileText className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-medium">{template.title}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {template.category ? (
                          <Badge variant="secondary" className="capitalize">
                            {getCategoryLabel(template.category)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-muted-foreground max-w-xs">
                          {truncateText(template.body, 80)}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-3 h-3 text-muted-foreground" />
                          <span className="text-sm">{formatDate(template.createdAt)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Edit"
                            onClick={() => {
                              handleEditTemplate(template);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Duplicate"
                            onClick={() => {
                              handleCopyTemplate(template);
                            }}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Delete"
                            className="text-red-600"
                            onClick={() => {
                              void handleDeleteTemplate(template._id);
                            }}
                            disabled={isSubmitting}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {(currentPage - 1) * 10 + 1} to {Math.min(currentPage * 10, totalCount)}{" "}
                    of {totalCount} results
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        void loadTemplates(currentPage - 1);
                      }}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        void loadTemplates(currentPage + 1);
                      }}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Template Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="w-[100vw] max-w-5xl">
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
            <DialogDescription>Update the notification template</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mb-6">
            <div className="space-y-2">
              <Label htmlFor="editTitle">Title *</Label>
              <Input
                id="editTitle"
                placeholder="Enter template title..."
                value={formData.title}
                onChange={e => {
                  setFormData(prev => ({ ...prev, title: e.target.value }));
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editCategory">Category</Label>
              <SelectField
                value={formData.category}
                onValueChange={(value: string) => {
                  setFormData(prev => ({ ...prev, category: value }));
                }}
                placeholder="Select category"
                options={TEMPLATE_CATEGORIES.map(category => ({
                  label: getCategoryLabel(category),
                  value: category,
                }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editBody">Message Body *</Label>
              <div className="bg-white rounded border">
                <ReactQuill
                  theme="snow"
                  value={formData.body}
                  onChange={(value: string) => {
                    setFormData(prev => ({ ...prev, body: value }));
                  }}
                  style={{ height: 500 }}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateTemplate}
              style={{ backgroundColor: currentTheme.colors.primary }}
              disabled={isSubmitting}
            >
              <Save className="w-4 h-4 mr-2" />
              Update Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
